import { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { EnrollmentFeeSection, TuitionOptionsEditor, TuitionPaymentOptionsSection } from './TuitionOptionsEditor';
import { isCurrentYear } from '~/utils/schoolYearState';
import { openingPricesYear } from './pricesYear';
import type { SettingsSchoolYear } from './types';

function yearLabel(year: SettingsSchoolYear): string {
  return isCurrentYear(year) ? `${year.name} (current year)` : year.name;
}

interface TuitionAndFeesProps {
  years: readonly SettingsSchoolYear[];
  onSaved?: () => void;
}

/** Tuition options, payment plans and the enrollment fee of one school year, picked from the school's years. */
export function TuitionAndFees({ years, onSaved }: TuitionAndFeesProps) {
  const [yearId, setYearId] = useState(() => openingPricesYear(years)?.id);
  // A year that is no longer listed (or a list that arrived late) opens the default again.
  useEffect(() => {
    if (!years.some((year) => year.id === yearId)) setYearId(openingPricesYear(years)?.id);
  }, [years, yearId]);

  const year = years.find((candidate) => candidate.id === yearId);
  if (!year) return <p className="text-sm text-muted-foreground">This school has no school years yet.</p>;

  const tuitionOptions = year.tuitionOptionsSetting;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">{`Prices for ${yearLabel(year)}`}</h3>
          <Select value={year.id} onValueChange={setYearId}>
            <SelectTrigger className="w-auto min-w-40" aria-label="School year">
              <SelectValue placeholder="Select a year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((candidate) => (
                <SelectItem key={candidate.id} value={candidate.id}>
                  {yearLabel(candidate)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">
          Each school year has its own prices. A new school year starts with the previous year&apos;s.
        </p>
      </div>

      {/* Keyed by year so switching years drops another year's unsaved edits. */}
      <div className="space-y-6" key={year.id}>
        <TuitionOptionsEditor schoolYearId={year.id} setting={tuitionOptions} onSaved={onSaved} />
        <TuitionPaymentOptionsSection
          schoolYearId={year.id}
          paymentOptions={year.tuitionPaymentOptionsSetting}
          tuitionOptions={tuitionOptions.items}
          onRefetch={onSaved}
        />
        <EnrollmentFeeSection schoolYearId={year.id} enrollmentFee={year.enrollmentFeeSetting} onRefetch={onSaved} />
      </div>
    </div>
  );
}

export default TuitionAndFees;
